import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MetricCard from './components/MetricCard';
import UserManagementTable from './components/UserManagementTable';
import SubscriptionAnalytics from './components/SubscriptionAnalytics';
import SystemConfiguration from './components/SystemConfiguration';
import AuditLog from './components/AuditLog';
import AlertNotifications from './components/AlertNotifications';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { getTierInfo } from '../../utils/tierConfig';
import { getMaxCreditsForTier } from '../../utils/planCredits';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [errorNotification, setErrorNotification] = useState(null);

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const USERS_PER_PAGE = 15;
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalCount, setUsersTotalCount] = useState(0);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersTier, setUsersTier] = useState('all');
  const [usersSortField, setUsersSortField] = useState(null);
  const [usersSortDir, setUsersSortDir] = useState('none');
  const [stats, setStats] = useState({
    activeSubscriptions: 0,
    usersTrend: '+0%',
    subsTrend: '+0%',
  });

  // ── Monthly Revenue state ──────────────────────────────────────────────────
  const [revenueData, setRevenueData] = useState({ revenue: 0, percentChange: '0.0' });

  // ── Recent Activity state (from audit_logs) ────────────────────────────────
  const [recentActivity, setRecentActivity] = useState([]);

  const loadUsers = async (page, search, tier, sortField, sortDir) => {
    try {
      setIsLoading(true);
      const [usersResult, countResult] = await Promise.all([
        supabase.rpc('get_admin_users', {
          p_search: search || null,
          p_tier: (tier === 'all' || !tier) ? null : tier,
          p_sort_field: sortField || 'created_at',
          p_sort_dir: (sortDir === 'none' || !sortDir) ? 'desc' : sortDir,
          p_limit: USERS_PER_PAGE,
          p_offset: (page - 1) * USERS_PER_PAGE,
        }),
        supabase.rpc('get_admin_users_count', {
          p_search: search || null,
          p_tier: (tier === 'all' || !tier) ? null : tier,
        }),
      ]);
      if (usersResult.error) throw usersResult.error;
      const mapped = (usersResult.data || []).map(u => ({
        id: u.id,
        name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || 'No Name',
        firstName: u.first_name ?? '',
        lastName: u.last_name ?? '',
        username: u.username || '',
        email: u.email,
        subscription: u.subscription_tier || 'free',
        status: u.status || 'active',
        role: u.role || 'user',
        avatarUrl: u.avatar_url || '',
        credits: u.remaining_credits ?? 0,
        joinDate: u.created_at,
        lastActivity: u.last_activity,
        subscriptionStartDate: u.sub_start_date ?? null,
        subscriptionEndDate: u.sub_end_date ?? null,
        subscriptionStatus: u.sub_status ?? null,
        billingAmount: u.billing_amount ?? null,
        cancelledAt: u.cancelled_at ?? null,
        cancelledBy: u.cancelled_by ?? null,
        cancelledByName: u.cancelled_by_name ?? null,
      }));
      setUsers(mapped);
      setUsersTotalCount(countResult.data ?? 0);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers(1, '', 'all', null, 'none');

    const fetchStats = async () => {
      try {
        const now = new Date();
        const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

        // ── Active Subscriptions count (current) ──────────────────────────────
        // แยก 2 query เพื่อหลีกเลี่ยง FK ambiguity
        const { data: activeSubUsers } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'active');

        const activeSubUserIds = activeSubUsers?.map(s => s.user_id).filter(Boolean) ?? [];

        let activeSubCount = 0;
        if (activeSubUserIds.length > 0) {
          const { count } = await supabase
            .from('user_profiles')
            .select('id', { count: 'exact', head: true })
            .not('subscription_tier', 'in', '("free","enterprise","team_member")')
            .in('id', activeSubUserIds);
          activeSubCount = count ?? 0;
        }

        // ── Total Users trending ───────────────────────────────────────────────
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();

        // users ใหม่เดือนนี้
        const { count: thisMonthUsers } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfThisMonth);

        // users ใหม่เดือนที่แล้ว
        const { count: lastMonthUsers } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfLastMonth)
          .lt('created_at', startOfThisMonth);

        // % เปลี่ยนแปลง = (เดือนนี้ - เดือนที่แล้ว) / เดือนที่แล้ว × 100
        const usersTrendNum = (lastMonthUsers ?? 0) > 0
          ? (((thisMonthUsers - lastMonthUsers) / lastMonthUsers) * 100).toFixed(1)
          : (thisMonthUsers ?? 0) > 0 ? '100.0' : '0';
        const usersTrendStr = parseFloat(usersTrendNum) >= 0 ? `+${usersTrendNum}%` : `${usersTrendNum}%`;

        // ── Active Subscriptions trending ─────────────────────────────────────
        // subscriptions ใหม่เดือนนี้ (status = active)
        const { data: thisMonthSubUsers } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'active')
          .gte('start_date', startOfThisMonth);

        const thisMonthSubIds = thisMonthSubUsers?.map(s => s.user_id).filter(Boolean) ?? [];
        let thisMonthSubs = 0;
        if (thisMonthSubIds.length > 0) {
          const { count } = await supabase
            .from('user_profiles')
            .select('id', { count: 'exact', head: true })
            .not('subscription_tier', 'in', '("free","enterprise","team_member")')
            .in('id', thisMonthSubIds);
          thisMonthSubs = count ?? 0;
        }

        // subscriptions ใหม่เดือนที่แล้ว (status = active)
        const { data: lastMonthSubUsers } = await supabase
          .from('user_subscriptions')
          .select('user_id')
          .eq('status', 'active')
          .gte('start_date', startOfLastMonth)
          .lt('start_date', startOfThisMonth);

        const lastMonthSubIds = lastMonthSubUsers?.map(s => s.user_id).filter(Boolean) ?? [];
        let lastMonthSubs = 0;
        if (lastMonthSubIds.length > 0) {
          const { count } = await supabase
            .from('user_profiles')
            .select('id', { count: 'exact', head: true })
            .not('subscription_tier', 'in', '("free","enterprise","team_member")')
            .in('id', lastMonthSubIds);
          lastMonthSubs = count ?? 0;
        }

        // % เปลี่ยนแปลง = (เดือนนี้ - เดือนที่แล้ว) / เดือนที่แล้ว × 100
        const subsTrendNum = lastMonthSubs > 0
          ? (((thisMonthSubs - lastMonthSubs) / lastMonthSubs) * 100).toFixed(1)
          : thisMonthSubs > 0 ? '100.0' : '0';
        const subsTrend = parseFloat(subsTrendNum) >= 0 ? `+${subsTrendNum}%` : `${subsTrendNum}%`;

        setStats({
          activeSubscriptions: activeSubCount || 0,
          usersTrend: usersTrendStr,
          subsTrend: subsTrend,
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, []);

  // ── Fetch Monthly Revenue from revenue_records ─────────────────────────────
  useEffect(() => {
    const fetchMonthlyRevenue = async () => {
      try {
        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
        const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString();

        // Current month revenue
        const { data: currentData } = await supabase
          .from('revenue_records')
          .select('amount')
          .eq('status', 'success')
          .gte('paid_at', currentMonthStart)
          .lt('paid_at', nextMonthStart);

        // Previous month revenue
        const { data: prevData } = await supabase
          .from('revenue_records')
          .select('amount')
          .eq('status', 'success')
          .gte('paid_at', prevMonthStart)
          .lt('paid_at', currentMonthStart);

        const currentRevenue = (currentData || []).reduce((sum, r) => sum + Number(r.amount), 0);
        const prevRevenue = (prevData || []).reduce((sum, r) => sum + Number(r.amount), 0);

        let percentChange = 0;
        if (prevRevenue > 0) {
          percentChange = ((currentRevenue - prevRevenue) / prevRevenue) * 100;
        } else if (currentRevenue > 0) {
          percentChange = 100;
        }

        setRevenueData({
          revenue: currentRevenue,
          percentChange: percentChange.toFixed(1),
        });
      } catch (err) {
        console.error('Error fetching monthly revenue:', err);
      }
    };

    fetchMonthlyRevenue();
  }, []);

  // ── Fetch Recent Activity from audit_logs ──────────────────────────────────
  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        const { data, error } = await supabase
          .from('audit_logs')
          .select('id, action_type, target_email, description, performed_by_label, created_at')
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) throw error;
        setRecentActivity(data || []);
      } catch (err) {
        console.error('Error fetching recent activity:', err);
      }
    };

    fetchRecentActivity();
  }, []);

  // map action_type prefix → dot color
  const getActivityDotColor = (actionType) => {
    if (!actionType) return 'bg-muted-foreground';
    if (actionType.startsWith('user_')) return 'bg-primary';
    if (actionType.startsWith('subscription_') || actionType.startsWith('plan_')) return 'bg-success';
    if (actionType.startsWith('payment_') || actionType.startsWith('revenue_')) return 'bg-warning';
    if (actionType.startsWith('config_') || actionType.startsWith('system_')) return 'bg-accent';
    return 'bg-muted-foreground';
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const metrics = [
    {
      title: 'Total Users',
      value: usersTotalCount.toString(),
      change: stats.usersTrend,
      changeType: parseFloat(stats.usersTrend) >= 0 ? 'positive' : 'negative',
      icon: 'Users',
      iconColor: 'bg-primary',
      trend: true
    },
    {
      title: 'Active Subscriptions',
      value: stats.activeSubscriptions.toString(),
      change: stats.subsTrend,
      changeType: parseFloat(stats.subsTrend) >= 0 ? 'positive' : 'negative',
      icon: 'Crown',
      iconColor: 'bg-accent',
      trend: true
    },
    {
      title: 'Monthly Revenue',
      value: `$${revenueData.revenue.toLocaleString()}`,
      change: `${parseFloat(revenueData.percentChange) >= 0 ? '+' : ''}${revenueData.percentChange}%`,
      changeType: parseFloat(revenueData.percentChange) > 0 ? 'positive' : parseFloat(revenueData.percentChange) < 0 ? 'negative' : 'neutral',
      icon: 'DollarSign',
      iconColor: 'bg-success',
      trend: true
    },
    {
      title: 'System Usage',
      value: '--%',
      change: '+0%',
      changeType: 'neutral',
      icon: 'Activity',
      iconColor: 'bg-warning',
      trend: true
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'users', label: 'User Management', icon: 'Users' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'configuration', label: 'Configuration', icon: 'Settings' },
    { id: 'audit', label: 'Audit Log', icon: 'FileText' },
    { id: 'alerts', label: 'Alerts', icon: 'Bell' }
  ];

  const refreshUsers = async () => {
    await loadUsers(usersPage, usersSearch, usersTier, usersSortField, usersSortDir);
  };

  const handleUsersFilterChange = (search, tier, sortField, sortDir) => {
    setUsersSearch(search);
    setUsersTier(tier);
    setUsersSortField(sortField);
    setUsersSortDir(sortDir);
    setUsersPage(1);
    loadUsers(1, search, tier, sortField, sortDir);
  };

  const handleUsersPageChange = (page) => {
    setUsersPage(page);
    loadUsers(page, usersSearch, usersTier, usersSortField, usersSortDir);
  };

  const handleUserAction = async (action, userId, extraData) => {
    try {
      if (action === 'editSave' && extraData) {
        let avatarUrl = extraData.avatar_url;

        // ถ้ามีไฟล์ใหม่ → อัปโหลดไป Supabase Storage ก่อน
        if (extraData.avatar_file) {
          const file = extraData.avatar_file;
          const fileExt = file.name.split('.').pop();
          const filePath = `${userId}/${Date.now()}.${fileExt}`;

          const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, { upsert: true });

          if (uploadError) throw uploadError;

          const { data: publicUrlData } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

          avatarUrl = publicUrlData?.publicUrl || avatarUrl;
        }

        const { error } = await supabase
          .from('user_profiles')
          .update({
            first_name: extraData.first_name,
            last_name: extraData.last_name,
            username: extraData.username || null,
            role: extraData.role,
            avatar_url: avatarUrl,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (error) throw error;
        await refreshUsers();

      } else if (action === 'toggleStatus') {
        const targetUser = users.find(u => u.id === userId);
        const newStatus = targetUser?.status === 'active' ? 'inactive' : 'active';

        const { data: updatedData, error, count } = await supabase
          .from('user_profiles')
          .update({
            status: newStatus,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId)
          .select();

        if (error) throw error;

        if (!updatedData || updatedData.length === 0) {
          throw new Error('Permission denied: RLS policy บล็อกการ update — ต้องเพิ่ม admin UPDATE policy ใน Supabase');
        }

        await refreshUsers();

      } else if (action === 'subscriptionSave' && extraData) {
        // ดึง max_credits จาก subscription_plans table
        const maxCredits = await getMaxCreditsForTier(extraData.subscription_tier);

        // Fetch current profile for upgrade/downgrade credit logic
        const { data: currentProfile } = await supabase
          .from('user_profiles')
          .select('subscription_tier, monthly_credits, remaining_credits')
          .eq('id', userId)
          .single();

        const oldTier = currentProfile?.subscription_tier || 'free';
        const oldMax = currentProfile?.monthly_credits ?? 0;
        const oldRemaining = currentProfile?.remaining_credits ?? 0;
        const tierChanged = oldTier !== extraData.subscription_tier;
        const creditsChanged = oldMax !== maxCredits;

        let newRemaining;
        if (!tierChanged && !creditsChanged) {
          newRemaining = oldRemaining;
        } else if (oldTier === 'free' || oldMax === 0) {
          newRemaining = maxCredits;
        } else if (maxCredits > oldMax) {
          const used = oldMax - oldRemaining;
          newRemaining = Math.max(0, maxCredits - used);
        } else {
          newRemaining = Math.min(oldRemaining, maxCredits);
        }

        // If changing from team → other tier, transfer member work and delete members
        if (oldTier === 'team' && extraData.subscription_tier !== 'team') {
          const { data: activeMembers } = await supabase
            .from('team_members')
            .select('member_user_id')
            .eq('team_owner_id', userId)
            .eq('is_active', true);

          if (activeMembers && activeMembers.length > 0) {
            for (const m of activeMembers) {
              await supabase.from('projects').update({ user_id: userId }).eq('user_id', m.member_user_id);
              await supabase.from('saved_designs').update({ user_id: userId }).eq('user_id', m.member_user_id);
              await supabase.from('generation_metadata').update({ user_id: userId }).eq('user_id', m.member_user_id);
              await supabase.from('team_members').update({ is_active: false, updated_at: new Date().toISOString() }).eq('member_user_id', m.member_user_id);
              await supabase.rpc('delete_user_completely', { p_user_id: m.member_user_id });
            }
            const memberIds = activeMembers.map(m => m.member_user_id);
            setUsers(prev => prev.filter(u => !memberIds.includes(u.id)));
          }
        }

        // 1) UPDATE subscription_tier + credits ใน user_profiles
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            subscription_tier: extraData.subscription_tier,
            monthly_credits: maxCredits,
            remaining_credits: newRemaining,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (profileError) throw profileError;

        // 2) หา subscription record ที่มีอยู่ (active หรือล่าสุด)
        const { data: existingSubs } = await supabase
          .from('user_subscriptions')
          .select('id')
          .eq('user_id', userId)
          .order('start_date', { ascending: false })
          .limit(1);

        // ถ้า status เป็น cancelled → auto-fill cancelled_at/cancelled_by
        let cancelledAt = null;
        let cancelledBy = null;
        if (extraData.sub_status === 'cancelled') {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          cancelledAt = new Date().toISOString();
          cancelledBy = currentUser?.id || null;
        }

        const subPayload = {
          status: extraData.sub_status,
          start_date: extraData.start_date,
          end_date: extraData.end_date,
          billing_amount: extraData.billing_amount,
          cancelled_at: cancelledAt,
          cancelled_by: cancelledBy,
          updated_at: new Date().toISOString(),
        };

        if (existingSubs && existingSubs.length > 0) {
          // UPDATE record ที่มีอยู่
          const { error: subError } = await supabase
            .from('user_subscriptions')
            .update(subPayload)
            .eq('id', existingSubs[0].id);

          if (subError) throw subError;
        } else {
          // INSERT record ใหม่ถ้ายังไม่มี
          const { error: subError } = await supabase
            .from('user_subscriptions')
            .insert({ user_id: userId, ...subPayload });

          if (subError) throw subError;
        }

        await refreshUsers();

      } else if (action === 'delete') {
        const targetUser = users.find(u => u.id === userId);

        // Cancel Stripe subscription before deleting
        try {
          await supabase.functions.invoke('admin-cancel-stripe', { body: { userId } });
        } catch (stripeErr) {
          console.error('Stripe cancel error (non-blocking):', stripeErr);
        }

        // If team_member, transfer projects/designs/metadata to owner before deleting
        if (targetUser?.subscription === 'team_member') {
          const { data: teamRow } = await supabase
            .from('team_members')
            .select('team_owner_id')
            .eq('member_user_id', userId)
            .eq('is_active', true)
            .maybeSingle();

          if (teamRow?.team_owner_id) {
            await supabase.from('projects').update({ user_id: teamRow.team_owner_id }).eq('user_id', userId);
            await supabase.from('saved_designs').update({ user_id: teamRow.team_owner_id }).eq('user_id', userId);
            await supabase.from('generation_metadata').update({ user_id: teamRow.team_owner_id }).eq('user_id', userId);
            await supabase.from('team_members').update({ is_active: false, updated_at: new Date().toISOString() }).eq('member_user_id', userId);
          }
        }

        // If team owner, transfer member projects to owner then delete all members first
        if (targetUser?.subscription === 'team') {
          const { data: activeMembers } = await supabase
            .from('team_members')
            .select('member_user_id')
            .eq('team_owner_id', userId)
            .eq('is_active', true);

          if (activeMembers && activeMembers.length > 0) {
            const memberIds = activeMembers.map(m => m.member_user_id);

            // Transfer each member's work to owner, then delete member
            for (const memberId of memberIds) {
              await supabase.from('projects').update({ user_id: userId }).eq('user_id', memberId);
              await supabase.from('saved_designs').update({ user_id: userId }).eq('user_id', memberId);
              await supabase.from('generation_metadata').update({ user_id: userId }).eq('user_id', memberId);
              await supabase.from('team_members').update({ is_active: false, updated_at: new Date().toISOString() }).eq('member_user_id', memberId);
              await supabase.rpc('delete_user_completely', { p_user_id: memberId });
            }

            // Remove deleted members from local state
            setUsers(prev => prev.filter(u => !memberIds.includes(u.id)));
          }
        }

        // ลบ user ทั้ง auth.users + user_profiles (CASCADE) ผ่าน RPC
        const { data: deleteResult, error: deleteError } = await supabase
          .rpc('delete_user_completely', { p_user_id: userId });

        if (deleteError) throw deleteError;

        // ลบออกจาก local state เฉพาะเมื่อ DB ลบสำเร็จแล้ว
        setUsers(prev => prev.filter(u => u.id !== userId));

      } else if (action === 'bulkDeactivate' || action === 'bulkActivate') {
        const ids = userId; // array of ids
        const newStatus = action === 'bulkDeactivate' ? 'inactive' : 'active';

        const { data: updatedRows, error: bulkStatusError } = await supabase
          .from('user_profiles')
          .update({ status: newStatus, updated_at: new Date().toISOString() })
          .in('id', ids)
          .select('id');

        if (bulkStatusError) throw bulkStatusError;

        if (!updatedRows || updatedRows.length === 0) {
          throw new Error('Permission denied: RLS policy is preventing this action.');
        }

        setUsers(prev =>
          prev.map(u => ids.includes(u.id) ? { ...u, status: newStatus } : u)
        );

      } else if (action === 'adjustCredits' && extraData) {
        const targetUser = users.find(u => u.id === userId);
        const current = targetUser?.credits ?? 0;
        const delta = extraData.type === 'add' ? extraData.amount : -extraData.amount;
        const newCredits = Math.max(0, current + delta);

        const { error: creditError } = await supabase
          .from('user_profiles')
          .update({ remaining_credits: newCredits, updated_at: new Date().toISOString() })
          .eq('id', userId);

        if (creditError) throw creditError;

        // If team_member, also adjust team owner's remaining_credits
        if (targetUser?.subscription === 'team_member') {
          const { data: teamRow } = await supabase
            .from('team_members')
            .select('team_owner_id')
            .eq('member_user_id', userId)
            .eq('is_active', true)
            .maybeSingle();

          if (teamRow?.team_owner_id) {
            const { data: ownerProfile } = await supabase
              .from('user_profiles')
              .select('remaining_credits')
              .eq('id', teamRow.team_owner_id)
              .single();

            if (ownerProfile) {
              const ownerNewCredits = Math.max(0, ownerProfile.remaining_credits + delta);
              await supabase
                .from('user_profiles')
                .update({ remaining_credits: ownerNewCredits, updated_at: new Date().toISOString() })
                .eq('id', teamRow.team_owner_id);

              // Update owner in local state if visible
              setUsers(prev =>
                prev.map(u => u.id === teamRow.team_owner_id ? { ...u, credits: ownerNewCredits } : u)
              );
            }
          }
        }

        setUsers(prev =>
          prev.map(u => u.id === userId ? { ...u, credits: newCredits } : u)
        );

      } else if (action === 'bulkDelete') {
        // userId คือ array ของ ids ในกรณี bulkDelete
        const ids = userId;
        const deletedMemberIds = [];

        for (const id of ids) {
          const targetUser = users.find(u => u.id === id);

          // Cancel Stripe subscription before deleting
          try {
            await supabase.functions.invoke('admin-cancel-stripe', { body: { userId: id } });
          } catch (stripeErr) {
            console.error('Stripe cancel error (non-blocking):', stripeErr);
          }

          // Transfer team_member projects to owner before deleting
          if (targetUser?.subscription === 'team_member') {
            const { data: teamRow } = await supabase
              .from('team_members')
              .select('team_owner_id')
              .eq('member_user_id', id)
              .eq('is_active', true)
              .maybeSingle();

            if (teamRow?.team_owner_id) {
              await supabase.from('projects').update({ user_id: teamRow.team_owner_id }).eq('user_id', id);
              await supabase.from('saved_designs').update({ user_id: teamRow.team_owner_id }).eq('user_id', id);
              await supabase.from('generation_metadata').update({ user_id: teamRow.team_owner_id }).eq('user_id', id);
              await supabase.from('team_members').update({ is_active: false, updated_at: new Date().toISOString() }).eq('member_user_id', id);
            }
          }

          // If team owner, transfer member projects to owner then delete all members first
          if (targetUser?.subscription === 'team') {
            const { data: activeMembers } = await supabase
              .from('team_members')
              .select('member_user_id')
              .eq('team_owner_id', id)
              .eq('is_active', true);

            if (activeMembers && activeMembers.length > 0) {
              const memberIds = activeMembers.map(m => m.member_user_id);
              for (const memberId of memberIds) {
                await supabase.from('projects').update({ user_id: id }).eq('user_id', memberId);
                await supabase.from('saved_designs').update({ user_id: id }).eq('user_id', memberId);
                await supabase.from('generation_metadata').update({ user_id: id }).eq('user_id', memberId);
                await supabase.from('team_members').update({ is_active: false, updated_at: new Date().toISOString() }).eq('member_user_id', memberId);
                await supabase.rpc('delete_user_completely', { p_user_id: memberId });
                deletedMemberIds.push(memberId);
              }
            }
          }
        }

        // ลบทีละคนผ่าน RPC (ลบทั้ง auth.users + CASCADE)
        for (const id of ids) {
          const { error: delErr } = await supabase
            .rpc('delete_user_completely', { p_user_id: id });
          if (delErr) throw delErr;
        }

        setUsers(prev => prev.filter(u => !ids.includes(u.id) && !deletedMemberIds.includes(u.id)));

      } else {
        console.log(`Action: ${action}, User ID: ${userId}`);
      }

      setShowSuccessNotification(true);
      setTimeout(() => setShowSuccessNotification(false), 3000);
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      setErrorNotification(error.message || 'เกิดข้อผิดพลาด');
      setTimeout(() => setErrorNotification(null), 5000);
      throw error; // re-throw เพื่อให้ modal รู้ว่า save ล้มเหลว → ไม่ปิด modal
    }
  };

  const handleSaveConfiguration = (config) => {
    console.log('Configuration saved:', config);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 3000);
  };

  const handleDismissAlert = (alertId) => {
    console.log('Alert dismissed:', alertId);
  };

  const handleResolveAlert = (alertId) => {
    console.log('Alert resolved:', alertId);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 3000);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header remainingCredits={0} monthlyLimit={0} />
      <main className="pt-20 lg:pt-24 pb-[20vh]">
        <div className="container mx-auto px-4">
          {showSuccessNotification && (
            <div className="fixed top-20 right-4 z-50 bg-success text-success-foreground px-6 py-3 rounded-lg shadow-floating flex items-center gap-2 animate-in slide-in-from-top">
              <Icon name="CheckCircle" size={20} />
              <span className="font-medium">Action completed successfully</span>
            </div>
          )}

          {errorNotification && (
            <div className="fixed top-20 right-4 z-50 bg-error text-error-foreground px-6 py-3 rounded-lg shadow-floating flex items-center gap-2 animate-in slide-in-from-top">
              <Icon name="AlertTriangle" size={20} />
              <span className="font-medium">{errorNotification}</span>
            </div>
          )}

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">Monitor and manage platform operations</p>
          </div>

          <div className="bg-card rounded-lg border border-border shadow-soft mb-6">
            <div className="flex items-center overflow-x-auto orange-scrollbar">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium transition-smooth border-b-2 whitespace-nowrap ${activeTab === tab?.id
                    ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                >
                  <Icon name={tab?.icon} size={20} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {metrics?.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-lg border border-border shadow-soft p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                    Recent Activity
                  </h3>
                  <div className="space-y-4">
                    {recentActivity.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
                    ) : (
                      recentActivity.map((item) => (
                        <div key={item.id} className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${getActivityDotColor(item.action_type)}`}></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground font-medium truncate">
                              {item.description || item.action_type?.replace(/_/g, ' ')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {item.target_email || item.performed_by_label || '—'} • {formatActivityTime(item.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="bg-card rounded-lg border border-border shadow-soft p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Zap" size={20} className="text-accent" />
                    Quick Actions
                  </h3>
                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="UserPlus"
                      iconPosition="left"
                      onClick={() => setActiveTab('users')}
                    >
                      Add New User
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Crown"
                      iconPosition="left"
                      onClick={() => {
                        setActiveTab('configuration');
                        setTimeout(() => {
                          document.getElementById('subscription-configuration')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 200);
                      }}
                    >
                      Manage Subscriptions
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Settings"
                      iconPosition="left"
                      onClick={() => setActiveTab('configuration')}
                    >
                      System Configuration
                    </Button>
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="FileText"
                      iconPosition="left"
                      onClick={() => setActiveTab('audit')}
                    >
                      View Audit Logs
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            isLoading ? (
              <div className="flex flex-col items-center justify-center p-12 bg-card rounded-lg border border-border shadow-soft">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">Fetching users from database...</p>
              </div>
            ) : (
              <UserManagementTable
                users={users}
                onUserAction={handleUserAction}
                totalCount={usersTotalCount}
                currentPage={usersPage}
                pageSize={USERS_PER_PAGE}
                onPageChange={handleUsersPageChange}
                onFilterChange={handleUsersFilterChange}
              />
            )
          )}


          {activeTab === 'analytics' && <SubscriptionAnalytics />}

          {activeTab === 'configuration' && (
            <SystemConfiguration onSaveConfiguration={handleSaveConfiguration} />
          )}

          {activeTab === 'audit' && <AuditLog />}

          {activeTab === 'alerts' && (
            <AlertNotifications
              onDismissAlert={handleDismissAlert}
              onResolveAlert={handleResolveAlert}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;